import { Component, OnInit, inject, Output, EventEmitter, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DocumentTypeService } from '../../services/document-type.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-doc-ref-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent],
  templateUrl: './doc-ref-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DocRefListComponent implements OnInit {
  types: any[] = [];
  filtered: any[] = [];
  searchText = '';
  activeFilter: 'all' | 'active' | 'inactive' = 'all';
  expireFilter: 'all' | 'yes' | 'no' = 'all';
  isLoading = true;
  rows = Array.from({ length: 5 }, (_, i) => i + 1);
  order = true;
  orderColName = 'name';
  selectedSlug = signal<string | null>(null);

  public appSettings = inject(AppSettings);
  private documentTypeService = inject(DocumentTypeService);
  readonly perm = inject(PermissionService);

  @Output() selectEvent = new EventEmitter<any>();

  ngOnInit(): void {
    this.loadTypes();
  }

  loadTypes(): void {
    this.isLoading = true;
    this.documentTypeService.get().subscribe({
      next: (res) => {
        this.types = res || [];
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  applyFilters(): void {
    const term = (this.searchText || '').trim().toLowerCase();
    this.filtered = this.types.filter((t) => {
      if (this.activeFilter === 'active' && t.isActive === false) return false;
      if (this.activeFilter === 'inactive' && t.isActive !== false) return false;
      if (this.expireFilter === 'yes' && !t.allowExpiration) return false;
      if (this.expireFilter === 'no' && t.allowExpiration) return false;
      if (!term) return true;
      return [t.name, t.slug, t.nameShort]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
    this.applyOrder();
  }

  orderCol(col: string): void {
    if (this.orderColName === col) this.order = !this.order;
    else { this.orderColName = col; this.order = true; }
    this.applyOrder();
  }

  applyOrder(): void {
    this.filtered.sort((a, b) => {
      const valA = a[this.orderColName];
      const valB = b[this.orderColName];
      if (typeof valA === 'string' && typeof valB === 'string') {
        return this.order ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return this.order ? (valA ?? 0) - (valB ?? 0) : (valB ?? 0) - (valA ?? 0);
    });
  }

  selectType(row: any): void {
    this.selectedSlug.set(row.slug);
    this.selectEvent.emit(row);
  }
}
